import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography, Popover } from '@mui/material';

// project import
import NavItem from './NavItem';
import { activeItem } from 'store/reducers/menu';

// assets
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { getTextColor } from 'utils/colorUtils';

// ==============================|| NAVIGATION - LIST COLLAPSE ||============================== //

const NavCollapse = ({ menu, level }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const { drawerOpen, openItem } = useSelector((state) => state.menu);
    const { pathname } = useLocation();

    // Check if this collapse menu or any of its children are selected
    // Only show parent as selected in mini drawer mode
    const isCollapseItemSelected = !drawerOpen && menu.id && openItem.findIndex((id) => id === menu.id) > -1;

    const handleClick = (event) => {
        if (!drawerOpen) {
            // For mini-drawer, show popover
            setAnchorEl(event.currentTarget);
        } else {
            // For full drawer, toggle collapse
            setOpen(!open);
        }
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const popoverOpen = Boolean(anchorEl);

    // Check if any child route is active to auto-expand the parent and mark parent as selected
    useEffect(() => {
        if (menu && menu.children) {
            // Recursively check if current path matches any child or grandchild
            const checkActiveChild = (items) => {
                if (!Array.isArray(items)) return false;

                for (let i = 0; i < items.length; i++) {
                    const item = items[i];
                    if (!item) continue;

                    // Direct match
                    if (item.url && pathname.includes(item.url)) {
                        return true;
                    }

                    // Check nested children (for multi-level menu)
                    if (item.children && Array.isArray(item.children)) {
                        if (checkActiveChild(item.children)) {
                            return true;
                        }
                    }
                }
                return false;
            };

            // If any child is active, expand this collapse
            const hasActiveChild = checkActiveChild(menu.children);
            if (hasActiveChild) {
                setOpen(true);

                // Add this collapse item's ID to the active items list ONLY if drawer is not open
                if (!drawerOpen && menu.id && openItem.findIndex((id) => id === menu.id) === -1) {
                    dispatch(activeItem({ openItem: [...openItem, menu.id] }));
                } else if (drawerOpen && menu.id && openItem.includes(menu.id)) {
                    // Remove parent from selection if drawer is open and parent is in selection
                    const filteredOpenItems = openItem.filter(id => id !== menu.id);
                    dispatch(activeItem({ openItem: filteredOpenItems }));
                }
            }
        }
    }, [pathname, menu, dispatch, openItem, drawerOpen]);

    const textColor = 'text.primary';
    const iconSelectedColor = theme.palette.secondary.main;
    const iconColor = theme.palette.secondary.light;
    const hoverColor = theme.palette.secondary.lighter;

    // Safety check for menu object
    if (!menu || typeof menu !== 'object') {
        return null;
    }

    const Icon = menu.icon;
    const menuIcon = menu.icon ? <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem', color: isCollapseItemSelected ? iconSelectedColor : iconColor }} /> : null;

    // Add safety checks for children array
    const safeChildren = menu.children && Array.isArray(menu.children) ? menu.children : [];

    // Create menu items - with a special version for popover items that closes the popover on selection
    const menus = safeChildren.map((item) => {
        if (!item || !item.type) {
            return null; // Skip invalid items
        }

        switch (item.type) {
            case 'collapse':
                return <NavCollapse key={item.id} menu={item} level={level + 1} />;
            case 'item':
                return <NavItem key={item.id} item={item} level={level + 1} />;
            default:
                return (
                    <Typography key={item.id || 'error-key'} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    }).filter(Boolean); // Filter out null items

    // Create versions of menu items that close the popover when clicked
    const popoverMenus = safeChildren.map((item) => {
        if (!item || !item.type) {
            return null; // Skip invalid items
        }
// For items in the popover, wrap them with click handling to close the popover
        switch (item.type) {
            case 'collapse':
                return (
                    <div
                        key={item.id}
                        onClick={handlePopoverClose}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handlePopoverClose(e);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <NavCollapse menu={item} level={level + 1} />
                    </div>
                );
            case 'item':
                return (
                    <div
                        key={item.id}
                        onClick={handlePopoverClose}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handlePopoverClose(e);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <NavItem item={item} level={level + 1} />
                    </div>
                );
            default:
                return (
                    <Typography key={item.id || 'error-key'} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    }).filter(Boolean);

    // If there are no valid menu items after filtering, don't render anything
    if (menus.length === 0) {
        return null;
    }

    return (
        <>
            <ListItemButton
                sx={{
                    zIndex: 1201,
                    pl: drawerOpen ? `${level * 28}px` : 1.5,
                    py: !drawerOpen && level === 1 ? 1.25 : 1,
                    marginLeft: 1,
                    marginRight: 1,
                    borderRadius: theme.borderRadius ? theme.borderRadius.sm : '8px',
                    ...(drawerOpen && {
                        '&:hover': {
                            bgcolor: hoverColor,
                            '& .MuiTypography-root': {
                                color: getTextColor(hoverColor),
                            },
                        },
                        '&.Mui-selected': {
                            bgcolor: hoverColor,
                            color: iconSelectedColor,
                            '&:hover': {
                                color: iconSelectedColor,
                                bgcolor: hoverColor
                            }
                        }
                    }),
                    ...(!drawerOpen && {
                        '&:hover': {
                            bgcolor: 'transparent'
                        },
                        '&.Mui-selected': {
                            '&:hover': {
                                bgcolor: 'transparent'
                            },
                            bgcolor: 'transparent'
                        }
                    })
                }}
                selected={isCollapseItemSelected}
                onClick={handleClick}
            >
                {menuIcon && (
                    <ListItemIcon
                        sx={{
                            minWidth: 28,
                            color: isCollapseItemSelected ? iconSelectedColor : textColor,
                            ...(!drawerOpen && {
                                borderRadius: theme.borderRadius ? theme.borderRadius.sm : '8px',
                                width: 36,
                                height: 36,
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': {
                                    bgcolor: hoverColor
                                }
                            }),
                            ...(!drawerOpen && isCollapseItemSelected && {
                                color: iconSelectedColor,
                                bgcolor: hoverColor,
                                '&:hover': {
                                    bgcolor: hoverColor
                                }
                            })
                        }}
                    >
                        {menuIcon}
                    </ListItemIcon>
                )}
                {(drawerOpen || (!drawerOpen && level !== 1)) && (
                    <ListItemText
                        primary={
                            <Typography variant="h6" color={isCollapseItemSelected ? iconSelectedColor : textColor}>
                                {menu.title}
                            </Typography>
                        }
                    />
                )}
                {(drawerOpen || (!drawerOpen && level !== 1)) && (open ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>

            {/* For expanded drawer - use existing collapse */}
            {drawerOpen && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ position: 'relative' }}>
                        {menus}
                    </List>
                </Collapse>
            )}

            {/* For mini drawer - use popover */}
            {!drawerOpen && (
                <Popover
                    open={popoverOpen}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    anchorOrigin={{
                        vertical: 'center',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'center',
                        horizontal: 'left',
                    }}
                    PaperProps={{
                        sx: {
                            borderRadius: theme.borderRadius ? theme.borderRadius.sm : '8px',
                            boxShadow: theme.customShadows.z1,
                            bgcolor: 'background.paper',
                            p: 1,
                            minWidth: 200,
                            '& .MuiListItemButton-root': {
                                borderRadius: theme.borderRadius ? theme.borderRadius.sm : '8px',
                                '&:hover': {
                                    bgcolor: hoverColor,
                                    '& .MuiTypography-root': {
                                        color: getTextColor(hoverColor),
                                    },
                                    '& .MuiListItemIcon-root': {
                                        color: theme.palette.secondary.main,
                                    }
                                }
                            }
                        }
                    }}
                >
                    <List component="div" disablePadding>
                        {popoverMenus}
                    </List>
                </Popover>
            )}
        </>
    );
};

NavCollapse.propTypes = {
    menu: PropTypes.object,
    level: PropTypes.number
};

export default NavCollapse;